import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { fetchInspections } from "../Reducers/actions";

const InspectionsPage = () => {
  const dispatch = useDispatch();
  const { inspections, loading } = useSelector(state => state.zwembadinspecties);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchParams] = useSearchParams();
  const companyIdFilter = searchParams.get('company_id');

  const currentYear = new Date().getFullYear();
  const [yearFilter, setYearFilter] = useState(currentYear.toString());

  // Generate year options (current year + 5 previous years + "all")
  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    yearOptions.push(y);
  }

  useEffect(() => {
    const filters = {};
    if (statusFilter) filters.status = statusFilter;
    if (companyIdFilter) filters.company_id = companyIdFilter;
    if (yearFilter) filters.year = yearFilter;
    dispatch(fetchInspections(filters));
  }, [dispatch, statusFilter, companyIdFilter, yearFilter]);

  // Group inspections by company
  const groupedInspections = inspections.reduce((acc, inspection) => {
    const companyId = inspection.company_id?._id || 'unknown';
    const companyName = inspection.company_id?.name || 'Onbekend zwembad';

    if (!acc[companyId]) {
      acc[companyId] = {
        name: companyName,
        inspections: []
      };
    }

    acc[companyId].inspections.push(inspection);
    return acc;
  }, {});

  // Sort companies alphabetically
  const sortedCompanies = Object.entries(groupedInspections).sort((a, b) =>
    a[1].name.localeCompare(b[1].name)
  );

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'badge-secondary',
      in_progress: 'badge-warning',
      completed: 'badge-success',
      approved: 'badge-primary',
      rejected: 'badge-danger'
    };
    const labels = {
      draft: 'Concept',
      in_progress: 'In uitvoering',
      completed: 'Voltooid',
      approved: 'Goedgekeurd',
      rejected: 'Afgekeurd'
    };
    return (
      <span className={`badge ${badges[status] || 'badge-secondary'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL');
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3>Inspecties</h3>
              <Link to="/zwembadinspecties/inspecties/nieuw" className="btn btn-primary">
                <i className="fas fa-plus"></i> Nieuwe Inspectie
              </Link>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-3">
                  <label className="small text-muted mb-1">Jaar</label>
                  <select
                    className="form-control"
                    value={yearFilter}
                    onChange={(e) => setYearFilter(e.target.value)}
                  >
                    <option value="">Alle jaren</option>
                    {yearOptions.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="small text-muted mb-1">Status</label>
                  <select
                    className="form-control"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">Alle statussen</option>
                    <option value="draft">Concept</option>
                    <option value="in_progress">In uitvoering</option>
                    <option value="completed">Voltooid</option>
                    <option value="approved">Goedgekeurd</option>
                    <option value="rejected">Afgekeurd</option>
                  </select>
                </div>
              </div>

              {loading ? (
                <div className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Laden...</span>
                  </div>
                </div>
              ) : inspections.length === 0 ? (
                <div className="alert alert-info">
                  Geen inspecties gevonden
                </div>
              ) : (
                <div>
                  {sortedCompanies.map(([companyId, company]) => (
                    <div key={companyId} className="mb-4">
                      <h4 className="mb-3">
                        <i className="fas fa-building mr-2 text-primary"></i>
                        {company.name}
                        <span className="badge badge-secondary ml-2">{company.inspections.length}</span>
                      </h4>
                      <div className="table-responsive">
                        <table className="table table-striped table-hover table-sm">
                          <thead className="thead-light">
                            <tr>
                              <th>Titel</th>
                              <th>Datum</th>
                              <th>Sjabloon</th>
                              <th>Inspecteur</th>
                              <th>Status</th>
                              <th>Laatst opgeslagen</th>
                              <th>Acties</th>
                            </tr>
                          </thead>
                          <tbody>
                            {company.inspections.map((inspection) => (
                              <tr key={inspection._id}>
                                <td>
                                  <strong>{inspection.title || '-'}</strong>
                                </td>
                                <td>{formatDate(inspection.inspection_date)}</td>
                                <td>
                                  {inspection.template_id?.name || '-'}
                                  <br />
                                  <small className="text-muted">{inspection.template_id?.category}</small>
                                </td>
                                <td>
                                  {inspection.inspector_id?.voornaam} {inspection.inspector_id?.achternaam}
                                </td>
                                <td>{getStatusBadge(inspection.status)}</td>
                                <td>
                                  <small className="text-muted">
                                    {formatDate(inspection.last_saved_at)}
                                  </small>
                                </td>
                                <td>
                                  <div className="btn-group" role="group">
                                    {(inspection.status === 'draft' || inspection.status === 'in_progress') && (
                                      <Link
                                        to={`/zwembadinspecties/inspecties/${inspection._id}/uitvoeren`}
                                        className="btn btn-sm btn-primary"
                                        title="Inspectie invullen"
                                      >
                                        <i className="fas fa-edit mr-1"></i>
                                        Invullen
                                      </Link>
                                    )}
                                    <Link
                                      to={`/zwembadinspecties/inspecties/${inspection._id}`}
                                      className="btn btn-sm btn-info"
                                      title="Bewerken"
                                    >
                                      <i className="fas fa-cog"></i>
                                    </Link>
                                    <Link
                                      to={`/zwembadinspecties/inspecties/${inspection._id}/rapport`}
                                      className="btn btn-sm btn-success"
                                      title="Rapport bekijken"
                                    >
                                      <i className="fas fa-file-pdf"></i>
                                    </Link>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InspectionsPage;
